import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const LineChartStructure = ({
  data,
  dataKeys = [],
  xAxisKey = "name",
  colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F'],
  title,
  xAxisLabel,
  yAxisLabel,
  currencySymbol = '£'
}) => {
  const [isMobile, setIsMobile] = useState(false);
  
  // Check viewport size on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile(); // Check on initial load
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white rounded-lg p-4">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Custom tooltip to format values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
          <p className="font-medium text-sm">{label}</p>
          <div className="space-y-1 mt-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center text-xs">
                <div
                  className="w-3 h-3 mr-2"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="mr-2">{entry.name}:</span>
                <span className="font-medium">{currencySymbol}{entry.value.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate appropriate height for the chart
  const getChartHeight = () => {
    // Simpler sizing model now that we've removed the in-chart legend
    return isMobile ? 300 : 350;
  };

  // Custom legend component for both mobile and desktop
  const CustomLegend = () => (
    <div className={`mt-4 flex flex-wrap gap-2 ${isMobile ? 'justify-center' : 'justify-center'}`}>
      {dataKeys.map((key, index) => (
        <div 
          key={key} 
          className="flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full"
          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        >
          <div
            className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} mr-1 rounded-full`}
            style={{ backgroundColor: colors[index % colors.length] }}
          ></div>
          <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
      {title && (
        <h3 className="text-lg font-semibold mb-4 text-center">{title}</h3>
      )}
      <div style={{ height: `${getChartHeight()}px` }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data} 
            margin={{ 
              top: 20, 
              right: 20, 
              left: isMobile ? 5 : 20, 
              bottom: xAxisLabel ? 40 : 25 // Simplified bottom margin
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey={xAxisKey} 
              label={xAxisLabel ? { 
                value: xAxisLabel, 
                position: 'bottom',
                offset: 20,
                style: { textAnchor: 'middle', fill: '#666', fontSize: isMobile ? 10 : 12 }
              } : null}
              tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }}
              tickMargin={8}
            />
            <YAxis
              label={yAxisLabel ? { 
                value: yAxisLabel, 
                angle: -90, 
                position: 'insideLeft',
                offset: isMobile ? -2 : -5,
                style: { textAnchor: 'middle', fill: '#666', fontSize: isMobile ? 10 : 12 }
              } : null}
              tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }}
              tickMargin={5}
              width={isMobile ? 35 : 60}
            />
            <Tooltip content={<CustomTooltip />} />
            {dataKeys.map((key, index) => (
              <Line 
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                activeDot={{ r: isMobile ? 6 : 8 }}
                name={key.charAt(0).toUpperCase() + key.slice(1)} // Capitalize the key name
                strokeWidth={isMobile ? 1.5 : 2}
                dot={{ strokeWidth: isMobile ? 1.5 : 2, r: isMobile ? 3 : 4 }}
                isAnimationActive={true}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Custom legend for all screen sizes */}
      <CustomLegend />
    </div>
  );
};

export default LineChartStructure;
